import React, { useEffect, useRef, useState } from "react";
import { MessageCardProps } from "./MessageCard.types";
import {
  Body5MediumTypography,
  Body5RegularTypography,
  Body6RegularTypography,
  Caps2SemiboldTypography,
  Heading2SemiboldTypography,
} from "../../../primitives/Typography/typographyVariants";
import { Icon } from "../../../components/Icon/Icon";
import FlexColumn from "../../../components/Layout/FlexColumn/FlexColumn";
import { colorPalette } from "../../../theme/colors";
import FlexRow from "../../../components/Layout/FlexRow/FlexRow";
import Box from "../../../primitives/Box/Box";
import { StyledImage } from "../../../components/Image/Image";
import Tags from "../../../components/Tags/Tags";
import {
  StyledImageContainer,
  StyledIcon,
  StyledVideo,
} from "./MessageCard.styles";
import { Button } from "../../../components/Button/Button";

export const MessageCard: React.FC<MessageCardProps> = ({
  isActionCompleted = false,
  title,
  subtitle,
  body,
  primaryButton,
  secondaryButton,
  actionType,
  timestamp,
  status,
  images,
  onClick,
  dataCy,
  media,
  isFeedback,
  onClickProduct,
  messageId,
  messageReferencedId,
  onReferenceClick,
  isReferenceLoading = false,
  footerClassName,
  templateFooter,
  width,
  maxWidth,
  onVideoPlay,
  onVideoPause,
  onVideoSoundToggle,
  onVideoSettingsClick,
  onVideoFullscreen,
  onVideoMinimize,
  onVideoPlaybackSpeed,
  onVideoDownload,
  onVideoPictureInPicture,
}) => {
  const videoId =
    messageId ||
    messageReferencedId ||
    `video-${Math.random().toString(36).substr(2, 9)}`;

  const hasReference = messageReferencedId || onReferenceClick !== undefined;

  const videoRef = useRef<HTMLVideoElement>(null);
  const previousVolumeRef = useRef<number>(1);
  const previousPlaybackRateRef = useRef<number>(1);
  const previousFullscreenRef = useRef<boolean>(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);

  const pauseAllOtherVideos = (currentVideoId: string) => {
    //Pause all ReactPlayer instances
    const allReactPlayers = document.querySelectorAll("[data-react-player]");
    allReactPlayers.forEach((player) => {
      const playerElement = player as HTMLElement;
      const playerId = playerElement.dataset.videoInstance;

      if (playerId && playerId !== currentVideoId) {
        // Find the ReactPlayer instance and pause it
        const reactPlayer = playerElement.querySelector("video");
        if (reactPlayer) {
          reactPlayer.pause();
          reactPlayer.currentTime = 0;
        }
      }
    });

    // Also pause any native video elements as fallback
    const allVideos = document.querySelectorAll("video[data-video-instance]");
    allVideos.forEach((video) => {
      const videoElement = video as HTMLVideoElement;
      if (videoElement.dataset.videoInstance !== currentVideoId) {
        videoElement.pause();
        videoElement.currentTime = 0;
        videoElement.dispatchEvent(new CustomEvent("videoPaused"));
      }
    });
  };

  //Use video-specific fullscreen change handler
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || media?.type !== "video") return;

    const handleVolumeChange = () => {
      const currentVolume = videoElement.volume;
      const isMuted = videoElement.muted;
      // Only track if volume actually changed (not just on initial load)
      if (previousVolumeRef.current !== (isMuted ? 0 : currentVolume)) {
        previousVolumeRef.current = isMuted ? 0 : currentVolume;
        onVideoSoundToggle?.();
      }
    };

    const handleRateChange = () => {
      const currentRate = videoElement.playbackRate;
      if (previousPlaybackRateRef.current !== currentRate) {
        previousPlaybackRateRef.current = currentRate;
        onVideoPlaybackSpeed?.(currentRate.toString());
      }
    };
    const handleFullscreenChange = () => {
      const currentPlayer = videoRef.current;
      if (currentPlayer) {
        const isCurrentlyFullscreen = !!(
          (document as any).fullscreenElement === currentPlayer ||
          (document as any).webkitFullscreenElement === currentPlayer ||
          (document as any).mozFullScreenElement === currentPlayer ||
          (document as any).msFullscreenElement === currentPlayer
        );

        if (isCurrentlyFullscreen && !previousFullscreenRef.current) {
          previousFullscreenRef.current = true;
          onVideoFullscreen?.();
        }

        // Track fullscreen exit (minimize)
        if (!isCurrentlyFullscreen && previousFullscreenRef.current) {
          previousFullscreenRef.current = false;
          onVideoMinimize?.();
        }

        if (!isCurrentlyFullscreen && isFullscreen) {
          // Video exited fullscreen
          setIsFullscreen(false);
          setIsVideoPlaying(false);
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
    };
  }, [
    isFullscreen,
    onVideoFullscreen,
    onVideoMinimize,
    onVideoSoundToggle,
    onVideoPlaybackSpeed,
  ]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement && media?.type === "video") {
      const handleTimeUpdate = () => {
        // Check if video is actually playing
        if (
          !videoElement.paused &&
          !videoElement.ended &&
          videoElement.readyState > 2
        ) {
          if (!isVideoPlaying) {
            setIsVideoPlaying(true);
            setIsVideoLoading(false);
          }
        }
      };

      videoElement.addEventListener("timeupdate", handleTimeUpdate);
      videoElement.addEventListener("playing", () => {
        setIsVideoPlaying(true);
        setIsVideoLoading(false);
      });

      return () => {
        videoElement.removeEventListener("timeupdate", handleTimeUpdate);
        videoElement.removeEventListener("playing", () => {
          setIsVideoPlaying(true);
          setIsVideoLoading(false);
        });
      };
    }
  }, [media?.src, media?.type, isVideoPlaying]);

  useEffect(() => {
    if (videoRef.current && media?.type === "video") {
      const videoElement = videoRef.current;

      // Force video to load metadata immediately
      videoElement.load();

      // Set a small timeout to show first frame
      setTimeout(() => {
        if (videoElement) {
          videoElement.currentTime = 0.1;
          setIsVideoLoading(false);
        }
      }, 100);
    }
  }, [media?.src, media?.type]);

  const renderMediaContent = () => {
    // Single media (image or video)
    if (media) {
      return (
        <FlexColumn
          alignItems="center"
          justifyContent="center"
          p={isFeedback ? 8 : 0}
          backgroundColor={
            isFeedback ? colorPalette.popWhite[500] : "transparent"
          }
          borderRadius={isFeedback ? "8px" : "0px"}
          onClick={(e) => {
            // Only trigger if clicking on the background (not on interactive elements)
            if (e.target === e.currentTarget) {
              onReferenceClick?.();
            }
          }}
        >
          {media?.type === "text" ? (
            <Box
              width="100%"
              onClick={(e) => {
                onReferenceClick?.();
                e.stopPropagation();
              }}
            >
              {(() => {
                const isUrl =
                  typeof media.text === "string" &&
                  (media.text.startsWith("http://") ||
                    media.text.startsWith("https://"));

                if (isUrl) {
                  return (
                    <Body5MediumTypography
                      color={colorPalette.info[500]}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (hasReference) {
                          onReferenceClick?.();
                        } else {
                          window.open(
                            media.text,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }
                      }}
                      textDecoration="underline"
                      cursor="pointer"
                      wordBreak="break-all"
                    >
                      {media.text}
                    </Body5MediumTypography>
                  );
                }

                // Regular text (not a URL)
                return (
                  <Body5RegularTypography color={colorPalette.popBlack[500]}>
                    {media.src}
                  </Body5RegularTypography>
                );
              })()}
            </Box>
          ) : media.type === "video" ? (
            <Box
              position="relative"
              borderRadius="4px"
              overflow="hidden"
              width="65%" // Match Footer.jsx width
              height="240px"
              backgroundColor={colorPalette.popWhite[100]}
              cursor="pointer"
            >
              {/*Use native HTML5 video with better preloading */}
              <StyledVideo
                isFullscreen={isFullscreen}
                ref={videoRef}
                data-video-instance={videoId}
                src={media.src}
                width="100%"
                height="100%"
                preload="metadata"
                muted
                controls={isVideoPlaying} //Show controls when playing
                onPlay={() => {
                  setIsVideoPlaying(true);
                  setIsVideoLoading(false);
                  setVideoError(false); //Clear any previous errors
                  onVideoPlay?.();
                }}
                onPause={() => {
                  setIsVideoPlaying(false);
                  onVideoPause?.();
                }}
                onEnded={() => {
                  setIsVideoPlaying(false);
                  setIsFullscreen(false);
                }}
                onLoadStart={() => {
                  setIsVideoLoading(true);
                }}
                onLoadedData={() => {
                  setIsVideoLoading(false);
                  setVideoError(false);
                }}
                onCanPlay={() => {
                  setIsVideoLoading(false);
                }}
                onError={(e) => {
                  setIsVideoPlaying(false);
                  setIsFullscreen(false);
                  setIsVideoLoading(false);
                  setVideoError(true);
                }}
                onLoadedMetadata={() => {
                  setIsVideoLoading(false);
                  // Force video to show first frame
                  if (videoRef.current) {
                    videoRef.current.currentTime = 0.1; // Small offset to show first frame
                  }
                }}
                onPlaying={() => {
                  // Additional event handler for when video actually starts playing
                  setIsVideoPlaying(true);
                  setIsVideoLoading(false);
                }}
                playsInline
                webkit-playsinline="true"
                x5-playsinline="true"
                x5-video-player-type="h5"
                x5-video-player-fullscreen="true"
                x5-video-orientation="portrait"
                poster={media.src}
              />
              {isVideoLoading && !videoError && (
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  backgroundColor={colorPalette.popWhite[100]}
                  zIndex={1}
                >
                  <Icon
                    name="fa-solid fa-spinner"
                    size="32"
                    height="32"
                    width="32"
                    color={colorPalette.popOrange[500]}
                    className="fa-spin"
                  />
                </Box>
              )}
              {videoError && (
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  backgroundColor={colorPalette.popWhite[100]}
                  zIndex={1}
                >
                  <FlexColumn alignItems="center" gap={8}>
                    <Icon
                      name="fa-solid fa-exclamation-triangle"
                      size="32"
                      height="32"
                      width="32"
                      color={colorPalette.error[500]}
                    />
                    <Body5RegularTypography color={colorPalette.error[500]}>
                      Video failed to load
                    </Body5RegularTypography>
                  </FlexColumn>
                </Box>
              )}
              {!isVideoPlaying && !isVideoLoading && !videoError && (
                <StyledIcon
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  borderRadius="50%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  onClick={() => {
                    onVideoPlay?.();
                    pauseAllOtherVideos(videoId);

                    setIsVideoPlaying(true);
                    setIsVideoLoading(false);

                    const videoElement = videoRef.current;
                    if (videoElement) {
                      // Request fullscreen
                      if (videoElement.requestFullscreen) {
                        videoElement
                          .requestFullscreen()
                          .then(() => {
                            setIsFullscreen(true);
                            videoElement.play().catch((error) => {
                              setIsVideoPlaying(false);
                            });
                          })
                          .catch((err) => {
                            // Fallback: play without fullscreen
                            videoElement.play().catch((error) => {
                              setIsVideoPlaying(false);
                            });
                          });
                      } else if (
                        (videoElement as any).webkitRequestFullscreen
                      ) {
                        (videoElement as any).webkitRequestFullscreen();
                        setIsFullscreen(true);
                        videoElement.play().catch((error) => {
                          setIsVideoPlaying(false);
                        });
                      } else if ((videoElement as any).mozRequestFullScreen) {
                        (videoElement as any).mozRequestFullScreen();
                        setIsFullscreen(true);
                        videoElement.play().catch((error) => {
                          setIsVideoPlaying(false);
                        });
                      } else if ((videoElement as any).msRequestFullscreen) {
                        (videoElement as any).msRequestFullscreen();
                        setIsFullscreen(true);
                        videoElement.play().catch((error) => {
                          setIsVideoPlaying(false);
                        });
                      } else {
                        // Fallback: play without fullscreen
                        videoElement.play().catch((error) => {
                          setIsVideoPlaying(false);
                        });
                      }
                    }
                  }}
                  zIndex={2}
                  width="60px"
                  height="60px"
                >
                  <Icon
                    name="fa-solid fa-circle-play"
                    size="32"
                    height="32"
                    width="32"
                    color={colorPalette.popOrange[500]}
                    cursor="pointer"
                  />
                </StyledIcon>
              )}
            </Box>
          ) : media.type === "image" ? (
            <Box
              height="160px"
              width="160px"
              borderRadius="4px"
              overflow="hidden"
              border={`1px solid ${colorPalette.popBlack[500]}`}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <StyledImage
                src={media.src}
                alt={media.alt}
                borderRadius="4px"
                objectFit="cover"
                height="100%"
                width="100%"
              />
            </Box>
          ) : (
            <></>
          )}
        </FlexColumn>
      );
    }
    // Multiple images gallery
    if (images && images.length > 0) {
      return (
        <FlexRow gap={8} alignItems="center" onClick={onClickProduct}>
          {images.slice(0, 3).map((image, index) => (
            <StyledImageContainer
              key={index}
              position="relative"
              borderRadius="4px"
              overflow="hidden"
              width="33%"
            >
              <StyledImage
                src={image.src}
                alt={image.alt || `Image ${index + 1}`}
                aspectRatio="1/1"
                objectFit="cover"
                width="100%"
                height="100%"
              />
              {index === 2 && images.length > 3 && (
                <FlexColumn
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  backgroundColor="rgba(0, 0, 0, 0.5)"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="4px"
                  gap={1}
                >
                  <Heading2SemiboldTypography
                    color={colorPalette.popWhite[500]}
                    textAlign="center"
                  >
                    +{images.length - 3}
                  </Heading2SemiboldTypography>
                  <Body5RegularTypography
                    color={colorPalette.popWhite[500]}
                    textAlign="center"
                  >
                    Products
                  </Body5RegularTypography>
                </FlexColumn>
              )}
            </StyledImageContainer>
          ))}
        </FlexRow>
      );
    }

    return null;
  };

  return (
    <FlexColumn
      pt={12}
      pb={4}
      px={12}
      borderRadius="8px"
      className="message-card-container"
      onClick={onClick}
      data-cy={dataCy}
      backgroundColor={colorPalette.popWhite[300]}
      width={width}
      maxWidth={maxWidth}
      gap={8}
      position="relative"
    >
      {isReferenceLoading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          borderRadius="8px"
          backgroundColor="rgba(255, 255, 255, 0.9)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={10}
        >
          <FlexColumn alignItems="center" gap={8}>
            <Icon
              name="fa-solid fa-spinner"
              size="24"
              height="24"
              width="24"
              color={colorPalette.popOrange[500]}
              className="fa-spin"
            />
            <Body6RegularTypography color={colorPalette.popBlack[400]}>
              Finding message...
            </Body6RegularTypography>
          </FlexColumn>
        </Box>
      )}
      {title && (
        <Caps2SemiboldTypography
          fontFamily="Lexend"
          color={colorPalette.popBlack[100]}
          className="message-card-title"
        >
          {title}
        </Caps2SemiboldTypography>
      )}
      {renderMediaContent()}

      {subtitle && (
        <Body5RegularTypography
          color={colorPalette.popBlack[500]}
          wordBreak="break-word"
          whiteSpace="pre-line"
        >
          {subtitle}
        </Body5RegularTypography>
      )}
      {body && (
        <FlexColumn gap={8}>
          {Object.entries(body)
            .filter(
              ([key, value]) =>
                value !== null && value !== undefined && value !== ""
            ) //Filter out null/undefined values
            .map(([key, value], index) => {
              const stringValue = String(value);
              const isUrl =
                typeof stringValue === "string" &&
                (stringValue.startsWith("http://") ||
                  stringValue.startsWith("https://"));
              return (
                <FlexRow
                  key={index}
                  gap={4}
                  alignItems="flex-start"
                  flexWrap="wrap"
                >
                  <Body5RegularTypography
                    color={colorPalette.popBlack[200]}
                    flexShrink={0}
                  >
                    {key}:
                  </Body5RegularTypography>
                  {isUrl ? (
                    <Body5MediumTypography
                      color={colorPalette.info[500]}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onReferenceClick?.();
                        window.open(
                          stringValue,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }}
                      textDecoration="underline"
                      cursor="pointer"
                      wordBreak="break-word"
                      whiteSpace="normal"
                      flex={1}
                      minWidth={0}
                    >
                      {stringValue}
                    </Body5MediumTypography>
                  ) : (
                    <Body5MediumTypography
                      color={colorPalette.popBlack[400]}
                      wordBreak="break-word"
                      whiteSpace="normal"
                      flex={1}
                      minWidth={0}
                    >
                      {stringValue}
                    </Body5MediumTypography>
                  )}
                </FlexRow>
              );
            })}
        </FlexColumn>
      )}

      {templateFooter && (
        <Body5RegularTypography color={colorPalette.popBlack[500]}>
          {templateFooter}
        </Body5RegularTypography>
      )}
      {(status || secondaryButton || primaryButton) && (
        <FlexRow
          justifyContent="space-between"
          gap={8}
          className={footerClassName}
        >
          {status && (
            <Tags
              type="collab-status"
              label={status.label}
              icon={status.icon}
              backgroundColor={status.backgroundColor}
              color={status.textColor}
              iconColor={status.iconColor}
            />
          )}
          {secondaryButton && (
            <Button
              variant="secondary"
              onClick={() => secondaryButton.onClick()}
              disabled={secondaryButton.disabled || isActionCompleted}
              size="small"
              width={status ? "auto" : "100%"}
              leftIcon={
                secondaryButton.leftIcon ? (
                  <Icon
                    size="12"
                    name={secondaryButton.leftIcon}
                    color="inherit"
                  />
                ) : undefined
              }
              rightIcon={
                secondaryButton.rightIcon ? (
                  <Icon
                    size="12"
                    name={secondaryButton.rightIcon}
                    color="inherit"
                  />
                ) : undefined
              }
            >
              {secondaryButton.text}
            </Button>
          )}
          {primaryButton && (
            <Button
              size="small"
              variant="primary"
              onClick={() => primaryButton.onClick()}
              disabled={primaryButton.disabled || isActionCompleted}
              width={status ? "auto" : "100%"}
              leftIcon={
                primaryButton.leftIcon ? (
                  <Icon
                    size="12"
                    name={primaryButton.leftIcon}
                    color="inherit"
                  />
                ) : undefined
              }
              rightIcon={
                primaryButton.rightIcon ? (
                  <Icon
                    size="12"
                    name={primaryButton.rightIcon}
                    color="inherit"
                  />
                ) : undefined
              }
            >
              {primaryButton.text}
            </Button>
          )}
        </FlexRow>
      )}
      {timestamp && (
        <FlexRow justifyContent="flex-end">
          <Body6RegularTypography color={colorPalette.popBlack[100]}>
            {timestamp}
          </Body6RegularTypography>
        </FlexRow>
      )}
    </FlexColumn>
  );
};
